import React, { useEffect, useState } from 'react';
import { Box, IconButton, MenuItem, Select, Slider, Tooltip, Typography } from '@mui/material';
import {
  CropSquare as AreaIcon,
  Explore as BearingIcon,
  MyLocation as RadiusIcon,
  PinDrop as CoordsIcon,
  RadioButtonUnchecked as RingsIcon,
  Straighten as DistanceIcon,
  Remove as MinimizeIcon,
} from '@mui/icons-material';
import { forward as mgrsForward } from 'mgrs';
import {
  MEASURE_RING_COUNT_MAX,
  MEASURE_RING_PRESETS_M,
  bearingDeg,
  formatAreaKm2,
  formatBearing,
  formatDistanceKm,
  haversineKm,
  pathLengthKm,
  polygonAreaKm2,
  type MeasureLngLat,
  type MeasureMode,
} from '../../services/mapMeasure';

export interface MapMeasureReadoutProps {
  mode: MeasureMode;
  onModeChange: (mode: MeasureMode) => void;
  points: MeasureLngLat[];
  ringSpacingM: number;
  onRingSpacing: (meters: number) => void;
  ringCount: number;
  onRingCount: (count: number) => void;
  /** 0–1. Range-ring fill and outline. */
  ringOpacity?: number;
  onRingOpacity?: (opacity: number) => void;
  right?: number;
  zIndex?: number;
}

const MODES: { id: MeasureMode; label: string; icon: React.ReactNode }[] = [
  { id: 'distance', label: 'Distance', icon: <DistanceIcon sx={{ fontSize: 16 }} /> },
  { id: 'area', label: 'Area', icon: <AreaIcon sx={{ fontSize: 16 }} /> },
  { id: 'radius', label: 'Radius', icon: <RadiusIcon sx={{ fontSize: 16 }} /> },
  { id: 'rings', label: 'Range rings', icon: <RingsIcon sx={{ fontSize: 16 }} /> },
  { id: 'bearing', label: 'Bearing', icon: <BearingIcon sx={{ fontSize: 16 }} /> },
  { id: 'coords', label: 'Coordinates', icon: <CoordsIcon sx={{ fontSize: 16 }} /> },
];

function toMgrs(lng: number, lat: number): string {
  try {
    return mgrsForward([lng, lat], 5);
  } catch {
    return '';
  }
}

// Dropdowns portal into the fullscreen element when the map is fullscreen;
// body-level portals would be hidden behind the fullscreen layer.
const menuProps = {
  sx: { zIndex: 4000 },
  container: () => (document.fullscreenElement as HTMLElement | null) ?? document.body,
};

const fieldSx = {
  height: 32,
  fontSize: '0.8rem',
  bgcolor: (t: { palette: { mode: string } }) =>
    t.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
  '& .MuiSelect-select': { py: 0.5 },
} as const;

const rowSx = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: 1.5,
  mt: 0.5,
  alignItems: 'center',
} as const;

const MapMeasureReadout: React.FC<MapMeasureReadoutProps> = ({
  mode,
  onModeChange,
  points,
  ringSpacingM,
  onRingSpacing,
  ringCount,
  onRingCount,
  ringOpacity = 0.85,
  onRingOpacity,
  right = 55,
  zIndex = 1100,
}) => {
  const pathKm = mode === 'distance' ? pathLengthKm(points) : 0;
  const areaKm2 = mode === 'area' ? polygonAreaKm2(points) : 0;
  const spacingLabel = ringSpacingM >= 1000 ? `${ringSpacingM / 1000} km` : `${ringSpacingM} m`;
  const pairKm = (mode === 'radius' || mode === 'bearing') && points.length >= 2
    ? haversineKm(points[0], points[1])
    : 0;
  const azimuth = mode === 'bearing' && points.length >= 2 ? bearingDeg(points[0], points[1]) : null;
  const [placeName, setPlaceName] = useState('');
  const [minimized, setMinimized] = useState(false);
  const current = MODES.find((m) => m.id === mode);

  // Short result for the minimized chip so the drawing stays readable
  // without the full card.
  const summary = (() => {
    if (mode === 'distance' && pathKm > 0) return formatDistanceKm(pathKm);
    if (mode === 'area' && areaKm2 > 0) return formatAreaKm2(areaKm2);
    if (mode === 'radius' && pairKm > 0) return formatDistanceKm(pairKm);
    if (mode === 'bearing' && azimuth != null) return formatBearing(azimuth);
    if (mode === 'rings' && points.length > 0) return `${ringCount} × ${spacingLabel}`;
    if (mode === 'coords' && points.length > 0) return `${points[0][1].toFixed(4)}, ${points[0][0].toFixed(4)}`;
    return current?.label ?? '';
  })();

  useEffect(() => {
    if (mode !== 'coords' || points.length < 1) {
      setPlaceName('');
      return;
    }
    const [lng, lat] = points[0];
    const token = process.env.REACT_APP_MAPBOX_TOKEN;
    if (!token) return;
    let cancel = false;
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?types=address,poi,neighborhood,locality,place,region&limit=1&access_token=${token}`;
    fetch(url)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancel) return;
        setPlaceName(data?.features?.[0]?.place_name || '');
      })
      .catch(() => {
        if (!cancel) setPlaceName('');
      });
    return () => {
      cancel = true;
    };
  }, [mode, points]);

  const surfaceSx = {
    position: 'absolute',
    top: 10,
    right,
    zIndex,
    backgroundColor: (t: { palette: { mode: string } }) =>
      t.palette.mode === 'dark' ? 'rgba(30, 30, 30, 0.95)' : 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(4px)',
    borderRadius: 1,
  } as const;

  if (minimized) {
    return (
      <Tooltip title="Show measure tool" placement="left">
        <Box
          role="button"
          onClick={(e) => {
            e.stopPropagation();
            setMinimized(false);
          }}
          sx={{
            ...surfaceSx,
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1,
            height: 32,
            cursor: 'pointer',
            color: 'text.primary',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          {current?.icon}
          <Typography variant="caption" fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>
            {summary}
          </Typography>
        </Box>
      </Tooltip>
    );
  }

  return (
    <Box
      onClick={(e) => e.stopPropagation()}
      sx={{ ...surfaceSx, p: 1.5, width: 196 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Select
          value={mode}
          size="small"
          fullWidth
          onChange={(e) => onModeChange(e.target.value as MeasureMode)}
          renderValue={(value) => {
            const m = MODES.find((x) => x.id === value);
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                {m?.icon}
                <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                  {m?.label}
                </Typography>
              </Box>
            );
          }}
          MenuProps={menuProps}
          sx={{
            height: 32,
            fontSize: '0.8rem',
            bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'),
            '& .MuiSelect-select': { display: 'flex', alignItems: 'center', py: 0.5 },
          }}
        >
          {MODES.map((m) => (
            <MenuItem key={m.id} value={m.id} dense sx={{ gap: 1, fontSize: '0.8rem' }}>
              <Box sx={{ display: 'flex', color: 'text.secondary' }}>{m.icon}</Box>
              {m.label}
            </MenuItem>
          ))}
        </Select>
        <Tooltip title="Minimize (keeps the drawing on the map)" placement="left">
          <IconButton
            size="small"
            aria-label="Minimize measure tool"
            onClick={() => setMinimized(true)}
            sx={{ p: 0.5, flexShrink: 0 }}
          >
            <MinimizeIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {mode === 'rings' && (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {points.length === 0 ? 'Click the map to set the center' : 'Center set. Click again to move it.'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.75, mt: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary">Spacing</Typography>
              <Select
                value={ringSpacingM}
                size="small"
                fullWidth
                onChange={(e) => onRingSpacing(Number(e.target.value))}
                MenuProps={menuProps}
                sx={fieldSx}
              >
                {MEASURE_RING_PRESETS_M.map((m) => (
                  <MenuItem key={m} value={m} dense sx={{ fontSize: '0.8rem' }}>
                    {m >= 1000 ? '1 km' : `${m} m`}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <Box sx={{ width: 72 }}>
              <Typography variant="caption" color="text.secondary">Rings</Typography>
              <Select
                value={ringCount}
                size="small"
                fullWidth
                onChange={(e) => onRingCount(Number(e.target.value))}
                MenuProps={menuProps}
                sx={fieldSx}
              >
                {Array.from({ length: MEASURE_RING_COUNT_MAX }, (_, i) => i + 1).map((n) => (
                  <MenuItem key={n} value={n} dense sx={{ fontSize: '0.8rem' }}>
                    {n}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </Box>
          {onRingOpacity && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Opacity {Math.round(ringOpacity * 100)}%
              </Typography>
              <Slider
                size="small"
                min={15}
                max={100}
                value={Math.round(ringOpacity * 100)}
                onChange={(_, v) => onRingOpacity((v as number) / 100)}
                valueLabelDisplay="auto"
                valueLabelFormat={(v) => `${v}%`}
                sx={{ mt: -0.25, mb: 0 }}
              />
            </Box>
          )}
          {points.length > 0 && (
            <Box sx={rowSx}>
              <Typography variant="caption" color="text.secondary">Out to</Typography>
              <Typography variant="caption" color="warning.main" fontWeight={700}>
                {formatDistanceKm((ringSpacingM * ringCount) / 1000)}
              </Typography>
            </Box>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontSize: '10px' }}>
            {ringCount} ring{ringCount === 1 ? '' : 's'} at {spacingLabel} apart.
          </Typography>
        </>
      )}

      {mode === 'distance' && (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {points.length < 2
              ? 'Click points along the line'
              : `${points.length} points · ${points.length - 1} segment${points.length > 2 ? 's' : ''}`}
          </Typography>
          {pathKm > 0 && (
            <Box sx={rowSx}>
              <Typography variant="caption" color="text.secondary">Distance</Typography>
              <Typography variant="caption" color="primary.main" fontWeight={700}>
                {formatDistanceKm(pathKm)}
              </Typography>
            </Box>
          )}
        </>
      )}

      {mode === 'area' && (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {points.length < 3
              ? 'Click corners of the shape'
              : `${points.length} corners`}
          </Typography>
          {areaKm2 > 0 && (
            <Box sx={rowSx}>
              <Typography variant="caption" color="text.secondary">Area</Typography>
              <Typography variant="caption" color="primary.main" fontWeight={700}>
                {formatAreaKm2(areaKm2)}
              </Typography>
            </Box>
          )}
        </>
      )}

      {mode === 'radius' && (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {points.length < 2 ? 'Click the center, then the edge' : 'Click again to move the edge'}
          </Typography>
          {pairKm > 0 && (
            <Box sx={rowSx}>
              <Typography variant="caption" color="text.secondary">Radius</Typography>
              <Typography variant="caption" color="primary.main" fontWeight={700}>
                {formatDistanceKm(pairKm)}
              </Typography>
            </Box>
          )}
        </>
      )}

      {mode === 'bearing' && (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {points.length < 2 ? 'Click the start, then the target' : 'Click again to move the target'}
          </Typography>
          {azimuth != null && (
            <>
              <Box sx={rowSx}>
                <Typography variant="caption" color="text.secondary">Azimuth</Typography>
                <Typography variant="caption" color="primary.main" fontWeight={700}>
                  {formatBearing(azimuth)}
                </Typography>
              </Box>
              <Box sx={rowSx}>
                <Typography variant="caption" color="text.secondary">Back azimuth</Typography>
                <Typography variant="caption" color="primary.main" fontWeight={700}>
                  {formatBearing((azimuth + 180) % 360)}
                </Typography>
              </Box>
              <Box sx={rowSx}>
                <Typography variant="caption" color="text.secondary">Distance</Typography>
                <Typography variant="caption" color="primary.main" fontWeight={700}>
                  {formatDistanceKm(pairKm)}
                </Typography>
              </Box>
            </>
          )}
        </>
      )}

      {mode === 'coords' && (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {points.length === 0 ? 'Click a point on the map' : 'Click again to move the point'}
          </Typography>
          {points.length > 0 && (
            <>
              <Box sx={rowSx}>
                <Typography variant="caption" color="text.secondary">Lat</Typography>
                <Typography variant="caption" fontWeight={700} sx={{ fontFamily: 'monospace' }}>
                  {points[0][1].toFixed(6)}
                </Typography>
              </Box>
              <Box sx={rowSx}>
                <Typography variant="caption" color="text.secondary">Lon</Typography>
                <Typography variant="caption" fontWeight={700} sx={{ fontFamily: 'monospace' }}>
                  {points[0][0].toFixed(6)}
                </Typography>
              </Box>
              <Box sx={rowSx}>
                <Typography variant="caption" color="text.secondary">MGRS</Typography>
                <Typography variant="caption" fontWeight={700} sx={{ fontFamily: 'monospace' }}>
                  {toMgrs(points[0][0], points[0][1]) || 'n/a'}
                </Typography>
              </Box>
              {placeName && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {placeName}
                </Typography>
              )}
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default MapMeasureReadout;
